/**
 * pageSvgs
 *
 * Seiten-Auflösung der OSMD-Vorschau: welches `<svg>` (= welche `GraphicalMusicPage`) liegt unter
 * einem Client-Punkt, und wie rechnet man zwischen Client-Pixeln und Sheet-Einheiten **dieser
 * Seite** um. Gemeinsame Quelle für Hit-Testing ({@link @/composables/osmd/useScoreHitTesting}),
 * Panel-Positionierung ({@link @/composables/osmd/useScoreHighlight}) und PDF-Export
 * ({@link @/composables/usePdfExport}).
 *
 * **Warum das überhaupt nötig ist (kritisch – nicht auf eine container-globale Rechnung
 * rückbauen):** OSMD gibt JEDER Seite denselben Ursprung. Im Bundle:
 *
 * ```js
 * setMusicPageAbsolutePosition(t,e){ return new PointF2D(0,0) }   // GraphicalMusicPage
 * ```
 *
 * und das ist die einzige Eingabe von `transformRelativeToAbsolutePosition()`. Jedes
 * `PositionAndShape.AbsolutePosition` ist damit **seiten-lokal** – OSMD dokumentiert das selbst
 * (`VexFlowBackend.d.ts`: „Each backend only renders one GraphicalMusicPage, to which the
 * coordinates are relative."). Bei `pageFormat: 'A4_P'` stapelt OSMD pro Seite ein eigenes
 * `<div id="osmdCanvasPage{n}"><svg id="osmdSvgPage{n}">` im Container; eine Rechnung gegen den
 * **Container** stimmt deshalb nur auf Seite 1 und trifft ab Seite 2 die Noten der ersten Seite.
 *
 * OSMDs eigenes `GraphicalMusicSheet.domToSvg()` hilft hier nicht: es benutzt hart
 * `this.drawer.Backends[0]`, also immer Seite 1. Der Ansatz (`createSVGPoint` + `getScreenCTM`)
 * ist aber richtig und wird hier **pro Seite** nachgebaut – er erledigt Zoom (OSMD setzt den Zoom
 * als viewBox, nicht als Gruppen-Transform), Scroll und jede CSS-Transform am Container in einem
 * Schritt.
 */
import {GraphicalNote, unitInPixels} from 'opensheetmusicdisplay';

/** Ein Punkt in Sheet-Einheiten (1 Einheit = `unitInPixels` px bei Zoom 1). */
export interface SheetPoint {
    x: number;
    y: number;
}

/** Alle gerenderten `<svg>`-Seiten aus dem OSMD-Container-DOM, in Seitenreihenfolge.
 *  Bewusst über das DOM und **nicht** über `osmd.drawer.Backends`: im gebundelten
 *  osmd-extended-Build lösen die Interna nicht zuverlässig auf (siehe `webapp/CLAUDE.md`). */
export function collectPageSvgs(container: Element | null | undefined): SVGSVGElement[] {
    return container ? Array.from(container.querySelectorAll('svg')) : [];
}

/** 0-basierter Seitenindex einer gerenderten Note (`PageNumber` ist 1-basiert); `-1` = unbekannt. */
export function pageIndexOfNote(note: GraphicalNote | null | undefined): number {
    const pageNumber = note?.ParentMusicPage?.PageNumber;
    return typeof pageNumber === 'number' && Number.isFinite(pageNumber) && pageNumber > 0
        ? pageNumber - 1
        : -1;
}

/** `createSVGPoint`/`getScreenCTM` sind in happy-dom (Vitest) und bei Nicht-SVG-Backends nicht
 *  verfügbar – dann `null`, und die Aufrufer fallen auf die Rect-Rechnung zurück. */
function transformViaCtm(svg: SVGSVGElement, x: number, y: number, invert: boolean): SheetPoint | null {
    try {
        if (typeof svg.getScreenCTM !== 'function' || typeof svg.createSVGPoint !== 'function') return null;
        const ctm = svg.getScreenCTM();
        if (!ctm) return null;
        const point = svg.createSVGPoint();
        point.x = x;
        point.y = y;
        const out = point.matrixTransform(invert ? ctm.inverse() : ctm);
        if (!Number.isFinite(out.x) || !Number.isFinite(out.y)) return null;
        return {x: out.x, y: out.y};
    } catch {
        return null;
    }
}

/** Client-Pixel → Sheet-Einheiten **dieser Seite** (Gegenstück zu {@link sheetUnitsToClient}). */
export function clientToSheetUnits(svg: SVGSVGElement, clientX: number, clientY: number, zoom: number): SheetPoint {
    const viaCtm = transformViaCtm(svg, clientX, clientY, true);
    if (viaCtm) return {x: viaCtm.x / unitInPixels, y: viaCtm.y / unitInPixels};
    const rect = svg.getBoundingClientRect();
    const scale = unitInPixels * (zoom || 1);
    return {x: (clientX - rect.left) / scale, y: (clientY - rect.top) / scale};
}

/** Sheet-Einheiten **dieser Seite** → Client-Pixel (Gegenstück zu {@link clientToSheetUnits}). */
export function sheetUnitsToClient(svg: SVGSVGElement, x: number, y: number, zoom: number): SheetPoint {
    const viaCtm = transformViaCtm(svg, x * unitInPixels, y * unitInPixels, false);
    if (viaCtm) return viaCtm;
    const rect = svg.getBoundingClientRect();
    const scale = unitInPixels * (zoom || 1);
    return {x: rect.left + x * scale, y: rect.top + y * scale};
}
